<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="main-header" v-translate>Search photos</h1>

    </div>

    <CCard class="no-borders">

      <CCardBody bodyWrapper>
        <CRow class="align-items-center justify-content-between">
          <CCol xl="auto" class="mb-3">
            <CForm @submit.prevent="search" class="flex-grow-1">

              <div class="d-md-flex flex-md-row">
                <div class="d-flex flex-row mb-3 mb-md-0 align-items-center">
                  <CInput
                      v-model="searchText"
                      :maxlength="canSearchByEmail ? 254 : 9"
                      required
                      :placeholder="searchPlaceholder"
                      class="mb-0 mr-2"
                      style="min-width: 280px">
                  </CInput>
                  <CButton type="submit" color="primary" class="mr-2">
                    <translate>Search</translate>
                  </CButton>
                </div>
                <ul class="list-inline d-flex mb-0 search-info-results align-items-center"
                    v-if="picaCodeData">
                  <li class="list-inline-item"><span class="badge badge-outline badge-primary pica-code">{{
                      textRaceNumber|upper
                    }}</span></li>
                  <li class="list-inline-item">
                    <a @click="copyToClipboard(picaCodeData.picacode, $gettext('Pica Code copied!'))"
                       class="badge badge-outline badge-primary pica-code"
                       style="padding-bottom: 3px;cursor: pointer">
                      <CIcon name="cip-code-clean" height="20"/>
                      {{ picaCodeData.picacode }}
                    </a>
                  </li>

                  <li class="list-inline-item" v-if="masterCheckinEmail">
                    <a @click="copyToClipboard(masterCheckinEmail, $gettext('Email copied!'))"
                       class="badge badge-outline badge-primary"
                       style="padding-bottom: 3px;cursor: pointer">
                      <CIcon name="cip-email" height="20"/>
                      {{ masterCheckinEmail }}
                    </a>
                  </li>
                </ul>
              </div>
            </CForm>
          </CCol>

          <CCol xl="auto" class="mb-3">
            <ul class="list-inline d-flex mb-0 search-info-results align-items-center"
                v-if="picaCodeData">
              <li class="list-inline-item">
                <span v-if="isPromo"><translate>Photo count</translate>: {{ photosCount }}</span>
                <span v-else><translate
                    :translate-params="{photosCountBought: photosCountBought, photosCount:photosCount}">Bought photos: %{photosCountBought} of %{photosCount}</translate></span>
              </li>
              <li class="list-inline-item" v-if="hasPerm('old_search_photo.download_hd')">
                <CLink v-if="!downloading" href="#" @click.prevent="download()"
                       :disabled="photosCountBought === 0">
                  <CIcon :style="{ color: photosCountBought > 0 ? 'inherit' : '#CDCDCD'}"
                         v-c-tooltip="{content: isPromo ? $gettext('Download all checkin photos'): $gettext('Download all bought photos'), placement: 'top-end', appendToBody: true}"
                         class="" name="cip-download-circle" height="34"/>
                </CLink>
                <CSpinner v-if="downloading" color="primary" size="sm" style="width:20px;height:20px;"/>

              </li>
            </ul>
          </CCol>
        </CRow>


        <div v-if="showNoPicaCodeFound || showNoCheckinFound || picaCodeData">
          <div v-if="showNoPicaCodeFound" class="text-danger mt-2 font-weight-semi-bold">
            <translate>Pica Code not found</translate>
          </div>
          <div v-if="showNoCheckinFound" class="text-danger mt-2 font-weight-semi-bold">
            <translate>No check-in found for this email</translate>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CAlert v-if="showNoMediasFound" show color="light" class="text-center">
      <h6 class="m-0" v-translate>Photos not found</h6>
    </CAlert>


    <div class="text-center pt-2" v-else-if="loadingMedias">
      <view-loading-message :loading="true"/>
    </div>


    <template v-else>
      <CRow>
        <CCol sm="12" v-if="medias" class="text-right">
          <ul class="list-inline mb-5 list-type-view">
            <li class="list-inline-item">
              <CLink @click.prevent="selectedTypeView=TYPE_VIEW.list"
                     :class="['mr-3', {'on': selectedTypeView===TYPE_VIEW.list}]">
                <CIcon name="cip-lines" width="24"/>
              </CLink>
            </li>
            <li class="list-inline-item">
              <CLink @click.prevent="selectedTypeView=TYPE_VIEW.grid"
                     :class="[{'on': selectedTypeView===TYPE_VIEW.grid}]">
                <CIcon name="cip-grid" width="20"/>
              </CLink>
            </li>
          </ul>
        </CCol>
        <CCol :sm="selectedTypeView === TYPE_VIEW.grid ? 6 : 12" v-for="(media, index) in medias" :key="index"
              class="mb-2">
          <CRow class="mb-1">
            <CCol>
              <ul class="list-inline d-flex mb-0 search-info-results-det justify-content-between align-items-center">
                <li class="list-inline-item">
                  <span v-if="media.shot_at">{{ media.shot_at|formatDateTime('LL HH:mm:ss', timeZone) }}</span>
                  <span v-else>-</span>
                </li>

                <li class="list-inline-item">
                  <span v-if="media.photographer">{{ media.photographer.email }}</span>
                  <span v-else>-</span>
                </li>
              </ul>
              <ul class="list-inline d-flex mb-0 search-info-results-det justify-content-between align-items-center">
                <li class="list-inline-item">
                  <span>ID: {{ media.id }}</span>
                </li>
                <li class="list-inline-item">
                  <span class="ml-1">{{ media.original_width }} x {{ media.original_height }}</span>
                </li>
              </ul>
            </CCol>
          </CRow>
          <CRow class="mb-2">
            <CCol>
              <figure class="figure position-relative w-100 order__figure rounded">
                <img class="w-100" :src="media.image_url">

                <!-- New Container for Media Actions (Add to Order, Bought badge, Favorites..) for side-by-side positioning  -->
                <div class="media-actions-container">
                    <!-- Favorites button (visible only if Dashboard user has dedicated permission) -->
                    <CButton
                        v-if="hasPerm('old_search_photo.favorite')"
                        class="btn-favorite"
                        size="sm"
                        color="light"
                        variant="outline"
                        @click="toggleFavorite(media)">
                        <CIcon
                            name="cipHeart"
                            height="20"
                            :class="media.is_favorite ? 'icon-full' : ''"/>
                    </CButton>

                    <!-- EVERYTHING ELSE REMAINS THE SAME, only moved inside the container.  -->
                    <CBadge color="success" class="text-uppercase badge-outline" v-if="media.is_bought">
                        <translate>Bought</translate>
                    </CBadge>
                    <CBadge color="info" class="text-uppercase badge-outline" v-if="isMediaDownloaded(media)">
                        <translate translate-context="photo_search.media">Downloaded</translate>
                    </CBadge>
                    <CButton class="btn-add-to-order" size="sm" color="primary"
                             v-if="!media.is_bought && hasPerm('old_search_photo.add_external_order') && !isPromo"
                             @click="askToAddMediaToOrder(media)" :disabled="media.loading">
                        <translate>Add to order</translate>
                    </CButton>
                </div>

                <CDropdown
                    class="no-arrow order__figure-actions"
                    v-if="canSeeDropdown"
                    placement="bottom-end">
                  <template #toggler-content>
                    <CHeaderNavLink>
                      <CIcon name="cip-more"/>
                    </CHeaderNavLink>
                  </template>
                  <CDropdownItem v-if="hasPerm('old_search_photo.change_order')" @click="askRemoveMediaPicacode(media)"
                                 :disabled="media.loading">
                    <translate>Remove</translate>
                  </CDropdownItem>
                  <CDropdownItem
                      v-if="!media.is_bought && hasPerm('old_search_photo.add_external_order') && !isPromo"
                      @click="askToAddMediaToOrder(media)" :disabled="media.loading">
                    <translate>Add to order</translate>
                  </CDropdownItem>
                  <CDropdownItem v-if="media.is_bought && hasPerm('old_search_photo.change_order') && !isPromo"
                                 @click="askRemoveMediaFromOrder(media)" :disabled="media.loading">
                    <translate>Remove from order</translate>
                  </CDropdownItem>
                  <CDropdownItem v-if="media.media_original_url" @click="download(media)">
                    <translate>Download HD</translate>
                  </CDropdownItem>
                </CDropdown>
                <template v-else-if="canDownloadHD(media)">
                  <a @click.prevent="download(media)" class="no-arrow order__figure-actions">
                    <CIcon
                        v-c-tooltip="{content: $gettext('Download HD'), placement: 'top-end', appendToBody: true}"

                        name="cip-download-circle" height="34"
                    />
                  </a>
                </template>


              </figure>
            </CCol>
          </CRow>

        </CCol>
      </CRow>

    </template>
  </div>
</template>

<script>
import PicaServiceService from "@/services/pica_service.service"
import MediaService from "@/services/media.service"
import CheckinService from "@/services/checkin.service"
import ViewLoadingMessage from "@/components/ViewLoadingMessage.vue";
import {mapGetters, mapActions} from "vuex";
import Downloader from "@/domain/core/utils/downloader";
import ConfigScope from "@/domain/core/constant/configScope";

const TYPE_VIEW = {
  list: 'list',
  grid: 'grid',
}

export default {
  name: "PicaCodeSearch",
  components: {ViewLoadingMessage},


  data() {
    return {
      searchText: '',
      showNoPicaCodeFound: false,
      showNoCheckinFound: false,
      showNoMediasFound: false,
      loadingMedias: false,

      picaCodeData: null,
      masterCheckinEmail: null,
      medias: null,

      selectedTypeView: null,

      downloading: false
    }
  },

  computed: {
    ...mapGetters('event',
        ['picaServiceId', 'isPromo', 'timeZone']
    ),
    ...mapGetters('nStats', ['downloadedMediaIds']),
    TYPE_VIEW() {
      return TYPE_VIEW
    },

    textRaceNumber() {
      return this.$gettext('Bib') + ': ' + this.picaCodeData.race_number
    },

    photosCount() {
      return this.medias !== null ? this.medias.length : 0
    },

    photosCountBought() {
      return this.medias !== null ? this.medias.filter(x => x.is_bought).length : 0
    },
    photosBoughtMediaIds() {
      // if promo download all photos otherwise only bought
      if (this.isPromo) {
        return this.medias !== null ? this.medias.map(x => x.id) : null
      } else {
        return this.medias !== null ? this.medias.filter(x => x.is_bought).map(x => x.id) : null
      }
    },
    picacodeQuery() {
      return this.$route.query.picacode
    },
    canSearchByEmail() {
      return this.hasPerm('checkin.fields.email')
    },
    searchPlaceholder() {
      if (this.canSearchByEmail) return this.$gettext('Pica Code, BIB number or email')
      return this.$gettext('Pica Code or BIB number')
    }
  },

  async mounted() {
    this.selectedTypeView = this.TYPE_VIEW.grid

    // Fetch permissions configurations: old_search_photo for the page, checkin for search by email
    await this.fetchConfig({ type: 'old_search_photo', id: this.picaServiceId, scope: ConfigScope.PICA_SERVICE })
    await this.fetchConfig({ type: 'checkin', id: this.picaServiceId, scope: ConfigScope.PICA_SERVICE })

    if (this.picacodeQuery) {
      this.searchText = this.picacodeQuery
      this.search()
    }
  },


  methods: {
    ...mapActions('login', ['fetchConfig']),
    ...mapActions('nStats', ['fetchDownloadedMediaIds', 'resetDownloadedMediaIds']),
    ...mapActions('downloader', ['downloadMedia']),
    copyToClipboard(value, successMessage) {
      navigator.clipboard.writeText(value)
      this.notifyMessage({text: successMessage, messageType: 'success'})
    },
    search() {
      this.showNoPicaCodeFound = false
      this.showNoCheckinFound = false
      this.showNoMediasFound = false
      this.loadingMedias = true
      this.medias = null
      this.masterCheckinEmail = null
      this.resetDownloadedMediaIds()

      if (this.canSearchByEmail && this.searchText.includes('@')) {
        this.searchByEmail()
        return
      }

      let pars = {}
      if (this.searchText.length === 9)
        pars.picacode = this.searchText
      else
        pars.raceNumber = this.searchText

      this.searchPicacodeReleased(pars)
    },
    searchByEmail() {
      CheckinService.getCheckins({pica_service: this.picaServiceId, email: this.searchText})
          .then((response) => {
            const checkin = response.data.results.find(item => item.is_master) || response.data.results[0]
            if (!checkin) {
              this.showNoCheckinFound = true
              return
            }
            // Show the badge immediately, without waiting for the checkin fetch by picacode
            if (checkin.is_master) this.masterCheckinEmail = checkin.email
            this.searchPicacodeReleased({picacode: checkin.picacode})
          }).finally(() => {
            this.loadingMedias = false
          })
    },
    searchPicacodeReleased(pars) {
      PicaServiceService.getPicacodeReleased(this.picaServiceId, pars)
          .then((response) => {
            if (response.data.count === 0) {
              this.showNoPicaCodeFound = true
            }

            if (response.data.count === 1) {
              this.picaCodeData = response.data.results[0]
              //this.searchText = ''

              const picaCode = this.picaCodeData.picacode

              CheckinService.getCheckins({pica_service: this.picaServiceId, picacode: picaCode})
                  .then((response) => {
                    const masterCheckin = response.data.results.find(checkin => checkin.is_master)
                    this.masterCheckinEmail = masterCheckin?.email || null

                    // Downloaded photos come from the metrics backend: a failure there must not break the search
                    const checkinUserId = masterCheckin?.user
                    if (checkinUserId)
                      this.fetchDownloadedMediaIds({picaServiceId: this.picaServiceId, userId: checkinUserId})
                          .catch(() => {})
                  })

              MediaService.getMedias({
                pica_service: this.picaServiceId,
                picacode: picaCode,
                media_type: 'photo',
                page_size: 1000,
                include_favorites: this.hasPerm('old_search_photo.favorite'),  // Include favorites only if Dashboard user has the right permission(s)
              })
                  .then((response) => {
                    if (response.data.count === 0) {
                      this.showNoMediasFound = true
                    }
                    this.medias = response.data.results
                  }).finally(() => {
                    this.loadingMedias = false
                  })
            }
          })
    },
    askRemoveMediaPicacode(media) {
      this.confirm({
        color: 'danger',
        title: this.$pgettext('photo_search', 'Remove photo'),
        message: this.$pgettext('photo_search', 'The selected photo will be deleted from the user\'s album. Do you want to proceed?'),
        cb_confirm: () => {
          this.removeMediaPicacode(media)
        }
      })
    },
    removeMediaPicacode(media) {
      this.$set(media, 'loading', true)
      MediaService.removeMediaPicacode(media.id, this.picaCodeData.picacode)
          .then(() => {
            this.medias = this.medias.filter(i => i.id !== media.id)
            this.notifyMessage({
              text: this.$gettextInterpolate(
                  this.$gettext('Photo ID %{mediaId} has been removed from %{picacode}'),
                  {mediaId: media.id, picacode: this.picaCodeData.picacode}),
              messageType: 'success'
            })
          })
          .catch(error => {
            this.notifyMessage({text: this.$gettext('Cannot remove photo'), messageType: 'warning'})
          }).finally(() => {
        this.$set(media, 'loading', false)
      })
    },
    askToAddMediaToOrder(media) {
      this.confirm({
        title: this.$pgettext('photo_search', 'Add photo to order'),
        message: this.$pgettext('photo_search', 'The selected photo will be visible without watermark in the user\'s album. Do you want to proceed?'),
        cb_confirm: () => {
          this.addMediaToOrder(media)
        }
      })
    },
    addMediaToOrder(media) {
      this.$set(media, 'loading', true)
      MediaService.addMediaToOrder(media.id, this.picaCodeData.picacode)
          .then(() => {
            this.$set(media, 'is_bought', true)
            this.notifyMessage({
              text: this.$gettextInterpolate(
                  this.$gettext('Photo ID %{mediaId} has been added to order'),
                  {mediaId: media.id}),
              messageType: 'success'
            })
          })
          .catch(error => {
            this.notifyMessage({text: this.$gettext('Cannot add photo to order'), messageType: 'warning'})
          }).finally(() => {
        this.$set(media, 'loading', false)
      })
    },
    askRemoveMediaFromOrder(media) {
      this.confirm({
        color: 'danger',
        title: this.$pgettext('photo_search', 'Remove photo from order'),
        message: this.$pgettext('photo_search', 'The selected photo will be removed from the user\'s order. Do you want to proceed?'),
        cb_confirm: () => {
          this.removeMediaFromOrder(media)
        }
      })
    },
    removeMediaFromOrder(media) {
      this.$set(media, 'loading', true)
      MediaService.removeMediaFromOrder(media.id, this.picaCodeData.picacode)
          .then(() => {
            this.$set(media, 'is_bought', false)
            this.notifyMessage({
              text: this.$gettextInterpolate(
                  this.$gettext('Photo ID %{mediaId} has been removed from order'),
                  {mediaId: media.id}),
              messageType: 'success'
            })
          })
          .catch(error => {
            this.notifyMessage({text: this.$gettext('Cannot remove photo from order'), messageType: 'warning'})
          }).finally(() => {
        this.$set(media, 'loading', false)
      })
    },

    async download(media) {
      let mediaIds = []

      media ? mediaIds = [media.id] : mediaIds = this.photosBoughtMediaIds

      if (!mediaIds || mediaIds.length === 0) return
      if (!media) this.downloading = true

      try {
        const result = await this.downloadMedia({
          name: `download-${this.picaCodeData.picacode}`,
          media: {
            ids: mediaIds,
            pica_service_id: this.picaServiceId,
          },
          options: {},
          count: mediaIds.length,
        })
        if (result && !result.success && !result.cancelled) {
          await this.notifyMessage({text: result.error || this.$gettext('Download failed'), messageType: 'danger'})
        }
      } finally {
        this.downloading = false
      }
    },
    canSeeDropdown() {
      return this.hasPerm('old_search_photo.admin_actions')
    },
    isMediaDownloaded(media) {
      // The metrics backend returns media ids as strings
      return (this.downloadedMediaIds || []).includes(String(media.id))
    },
    canDownloadHD(media) {
      if (!media.media_original_url) return false
      return media.is_bought
    },

    async toggleFavorite(media) {
      if (media.loading_favorite) return;
      
      if (!this.picaCodeData || !this.picaCodeData.picacode) {
        console.error('picaCodeData not available');
        this.notifyMessage({
          text: this.$gettext('Error: Code not available'),
          messageType: 'danger'
        });
        return;
      }
      
      this.$set(media, 'loading_favorite', true);
      
      try {
        const response = await MediaService.toggleFavorite(
            media.id, 
            this.picaCodeData.picacode,
            this.picaServiceId
        );
        
        // Update media state with server response
        this.$set(media, 'is_favorite', response.data.is_favorite);
        
        await this.notifyMessage({
          text: response.data.is_favorite 
            ? this.$gettext('Photo added to favorites') 
            : this.$gettext('Photo removed from favorites'),
          messageType: 'success'
        });
        
      } catch (error) {
        let errorMessage = this.$gettext('Error updating favorites');
        if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
        }
        
        await this.notifyMessage({
          text: errorMessage,
          messageType: 'danger'
        });
        
      } finally {
        this.$set(media, 'loading_favorite', false);
      }
    }
  }
}
</script>

